import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useAppHeader } from "@shared/components/AppHeaderContext";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Icon, LatLngBounds } from "leaflet";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Compass,
  X,
  Percent,
  CaretRight,
} from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { getCSSVariable, getLocalizedText } from "@shared/lib/utils";
// Stilurile hărții se încarcă la runtime, nu ca import static.
//
// Ca import obișnuit, Vite le contopea în foaia de stil principală, care
// blochează randarea pe TOATE paginile — inclusiv pe landing, care n-are
// nicio hartă. Așa ajung doar la cine deschide harta.
void import("leaflet/dist/leaflet.css");
import { getStoredUserSession } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/app/map")({
  component: MapPage,
});

// Obține culoarea offer din CSS
const offerColor = getCSSVariable("--color-primary", "#6D5EF7");

// Custom marker icon pentru oferte
const customIcon = new Icon({
  iconUrl: "data:image/svg+xml;base64," + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${offerColor}" width="32" height="32">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Custom marker icon pentru locația utilizatorului
const userLocationIcon = new Icon({
  iconUrl: "data:image/svg+xml;base64," + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40">
      <circle cx="12" cy="12" r="10" fill="${offerColor}" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

// Component pentru a centra harta
function FitBounds({ bounds }: { bounds: LatLngBounds | null }) {
  const map = useMap();

  useEffect(() => {
    if (bounds && map) {
      try {
        // Verifică dacă harta este inițializată corect
        if (map.getContainer() && map.getContainer().offsetParent !== null) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (error) {
        console.error("Error fitting bounds:", error);
      }
    }
  }, [bounds, map]);

  return null;
}


// Helper pentru calcularea distanței (Haversine)
function calculateDistanceKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Raza Pământului în km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Component pentru locația curentă
function LocationButton({
  onLocationUpdate,
  isActive,
}: {
  onLocationUpdate: (location: [number, number]) => void;
  isActive: boolean;
}) {
  const map = useMap();
  const currentLang = getCurrentLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRequestingRef = useRef(false);

  const handleSuccess = (position: GeolocationPosition) => {
    isRequestingRef.current = false;
    setIsLoading(false);
    
    try {
      const location: [number, number] = [
        position.coords.latitude,
        position.coords.longitude,
      ];
      if (map && map.getContainer()) {
        map.setView(location, 15);
        onLocationUpdate(location);
      }
    } catch (err) {
      console.error("Error setting map view:", err);
      setError(
        currentLang === "ro"
          ? "Eroare la centrarea hărții"
          : "Error centering map"
      );
    }
  };

  const handleError = (geoError: GeolocationPositionError, triedHighAccuracy: boolean) => {
    // Dacă low accuracy a eșuat și nu am încercat high accuracy, încearcă cu high accuracy
    if (!triedHighAccuracy && (geoError.code === geoError.POSITION_UNAVAILABLE || geoError.code === geoError.TIMEOUT)) {
      console.log("Low accuracy failed, trying high accuracy...");
      navigator.geolocation.getCurrentPosition(
        handleSuccess,
        (err) => handleError(err, true),
        {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0,
        }
      );
      return;
    }

    isRequestingRef.current = false;
    setIsLoading(false);
    
    let errorMessage = "";
    
    switch (geoError.code) {
      case geoError.PERMISSION_DENIED:
        errorMessage = currentLang === "ro"
          ? "Permisiunea pentru locație a fost refuzată. Activează permisiunea în setările browser-ului."
          : "Location permission denied. Enable location access in browser settings.";
        break;
      case geoError.POSITION_UNAVAILABLE:
        errorMessage = currentLang === "ro"
          ? "Locația nu este disponibilă. Verifică dacă serviciile de locație sunt activate în System Preferences → Security & Privacy → Location Services."
          : "Location unavailable. Check if location services are enabled in System Preferences → Security & Privacy → Location Services.";
        break;
      case geoError.TIMEOUT:
        errorMessage = currentLang === "ro"
          ? "Nu s-a putut obține locația. Verifică dacă serviciile de locație sunt activate pe Mac."
          : "Could not get location. Check if location services are enabled on your Mac.";
        break;
      default:
        errorMessage = currentLang === "ro"
          ? "Eroare la obținerea locației"
          : "Error getting location";
        break;
    }
    
    setError(errorMessage);
    alert(errorMessage);
  };

  const handleLocation = () => {
    // Evită apelurile multiple
    if (isRequestingRef.current || isLoading) {
      return;
    }

    if (!navigator.geolocation) {
      alert(
        currentLang === "ro"
          ? "Geolocația nu este suportată de browser-ul tău"
          : "Geolocation is not supported by your browser"
      );
      return;
    }

    isRequestingRef.current = true;
    setIsLoading(true);
    setError(null);

    // Încearcă mai întâi cu LOW accuracy (mai rapid, prin rețea/IP)
    // Apoi dacă eșuează, încearcă cu high accuracy
    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      (err) => handleError(err, false), // false = low accuracy first
      {
        enableHighAccuracy: false, // Low accuracy e mai rapid
        timeout: 30000, // 30 secunde timeout
        maximumAge: 300000, // Acceptă cache de până la 5 minute
      }
    );
  };

  return (
    <div className="absolute bottom-24 right-4 z-[1000]">
      <button
        onClick={handleLocation}
        disabled={isLoading}
        className={`p-3 rounded-full shadow-lg transition-all ${
          isActive ? "bg-primary" : "bg-white"
        } ${isLoading ? "opacity-50 cursor-wait" : "hover:scale-105"}`}
        title={currentLang === "ro" ? "Locația mea" : "My location"}
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <Compass
            size={20}
            className={`${isActive ? "text-white" : "text-primary"}`}
          />
        )}
      </button>
      {error && (
        <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-error/10 border border-error/20 rounded-lg text-xs text-error">
          {error}
        </div>
      )}
    </div>
  );
}

// Component pentru inițializarea hărții
function MapInitializer() {
  const map = useMap();

  useEffect(() => {
    // Asigură-te că harta este complet inițializată
    const timer = setTimeout(() => {
      try {
        if (map && map.getContainer()) {
          map.invalidateSize();
        }
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [map]);

  return null;
}

// Component wrapper pentru marker cu centrare automată
function CenteredMarker({
  position,
  icon,
  offer,
  onSelect,
}: {
  position: [number, number];
  icon: Icon;
  offer: any;
  onSelect: (offer: any) => void;
}) {
  const map = useMap();

  const handleClick = () => {
    if (map) {
      try {
        // Folosim metodele Leaflet pentru a calcula corect offset-ul
        // Convertim poziția marker-ului în pixeli pe hartă
        const markerPoint = map.latLngToContainerPoint(position);
        
        // Offset pentru header (aproximativ 180px de la top)
        const headerOffsetPixels = 180;
        
        // Calculează noua poziție în pixeli (mută marker-ul în jos)
        // markerPoint este deja un Point, deci putem folosi metodele sale
        const adjustedPoint = markerPoint.add([0, headerOffsetPixels]);
        
        // Convertim înapoi în coordonate geografice
        const adjustedLatLng = map.containerPointToLatLng(adjustedPoint);
        
        // Centrează harta pe poziția ajustată
        map.setView([adjustedLatLng.lat, adjustedLatLng.lng], 15, {
          animate: true,
          duration: 0.5,
        });
        
        // Selectează oferta pentru a afișa bara
        onSelect(offer);
      } catch (error) {
        console.error("Error centering map:", error);
        // Fallback la centrare simplă dacă calculul eșuează
        map.setView(position, 15, {
          animate: true,
          duration: 0.5,
        });
        onSelect(offer);
      }
    }
  };

  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={{
        click: handleClick,
      }}
    />
  );
}

// Component pentru schimbarea temei hărții
function MapThemeLayer({ theme }: { theme: string }) {
  const map = useMap();

  useEffect(() => {
    // Re-render harta când se schimbă tema
    if (map && map.getContainer()) {
      try {
        // Folosim setTimeout pentru a ne asigura că DOM-ul este gata
        const timer = setTimeout(() => {
          map.invalidateSize();
        }, 100);
        return () => clearTimeout(timer);
      } catch (error) {
        console.error("Error invalidating map size:", error);
      }
    }
  }, [theme, map]);

  const themeConfig = {
    default: {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      subdomains: "abc",
    },
    positron: {
      url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: "abcd",
    },
  };

  const config = themeConfig[theme as keyof typeof themeConfig] || themeConfig.default;

  return (
    <TileLayer
      attribution={config.attribution}
      url={config.url}
      subdomains={config.subdomains}
      maxZoom={19}
      minZoom={1}
      tileSize={256}
      zoomOffset={0}
      crossOrigin="anonymous"
    />
  );
}

function MapPage() {
  const currentLang = getCurrentLanguage();
  const { userId } = getStoredUserSession();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const { mapTheme, setMapTheme } = useAppHeader();
  const [locationActive, setLocationActive] = useState(false);
  const [nearestOffers, setNearestOffers] = useState<
    Array<{
      offer: any;
      distance: number;
    }>
  >([]);
  const [selectedOffer, setSelectedOffer] = useState<any | null>(null);

  // Nu mai cerem locația automat - doar când utilizatorul apasă butonul
  // Aceasta oferă o experiență mai bună și respectă mai bine permisiunile

  // Obține datele
  const offers = useQuery(api.offers.getActiveOffers, {});
  const categories = useQuery(api.offers.getCategories, {});

  // Obține datele utilizatorului
  const user = useQuery(
    api.auth.getCurrentUser,
    userId ? { userId } : "skip"
  );

  // Filtrează ofertele
  const filteredOffers = selectedCategory
    ? offers?.filter((o) => o.category?.slug === selectedCategory)
    : offers;

  // Calculează bounds
  const bounds = filteredOffers && filteredOffers.length > 0
    ? new LatLngBounds(
        filteredOffers
          .filter((o) => o.partner?.lat && o.partner?.lng)
          .map((o) => [o.partner!.lat!, o.partner!.lng!] as [number, number])
      )
    : null;

  // Default center (Iași)
  const defaultCenter: [number, number] = [47.1585, 27.6014];

  // Calculează ofertele cele mai apropiate când se activează locația
  useEffect(() => {
    if (locationActive && userLocation && filteredOffers) {
      const offersWithDistance = filteredOffers
        .filter((offer) => offer.partner?.lat && offer.partner?.lng)
        .map((offer) => ({
          offer,
          distance: calculateDistanceKm(
            userLocation[0],
            userLocation[1],
            offer.partner!.lat!,
            offer.partner!.lng!
          ),
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 3);

      setNearestOffers(offersWithDistance);
    } else {
      setNearestOffers([]);
    }
  }, [locationActive, userLocation, filteredOffers]);

  const handleLocationUpdate = (location: [number, number]) => {
    setUserLocation(location);
    setLocationActive(true);
  };

  // Salvează tema în localStorage când se schimbă și asigură-te că este validă
  useEffect(() => {
    if (mapTheme === "default" || mapTheme === "positron") {
      localStorage.setItem("mapTheme", mapTheme);
    } else {
      // Dacă tema nu este validă, resetează la default
      setMapTheme("default");
      localStorage.setItem("mapTheme", "default");
    }
  }, [mapTheme, setMapTheme]);

  if (!userId || offers === undefined || user === undefined || user === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }


  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden relative pt-[180px]">

      {/* Offer bar - apare sub header când se selectează un marker */}
      <AnimatePresence>
        {selectedOffer && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="absolute bottom-[calc(var(--safe-area-inset-top,0px)+100px)] left-0 right-0 z-50 px-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-border p-4">
              {/* Main content: Grid layout cu 2 coloane */}
              <div className="grid grid-cols-[auto_1fr_auto] gap-3 mb-3">
                {/* Left: Icon/Discount visual */}
                <div className="flex items-start pt-1">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Percent size={24} className="text-primary" />
                  </div>
                </div>

                {/* Center: Text content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-text text-lg">
                    {selectedOffer.partner?.name}
                  </h3>
                  {(selectedOffer.shortDescription || selectedOffer.description) && (
                    <p className="text-sm text-text-muted mt-0.5 leading-relaxed">
                      {getLocalizedText(selectedOffer.shortDescription || selectedOffer.description, currentLang)}
                    </p>
                  )}
                  <p className="text-xs text-text-light mt-1">
                    {selectedOffer.category?.name}
                  </p>
                  {selectedOffer.partner?.address && (
                    <p className="text-xs text-text-muted mt-1.5 flex items-center gap-1.5">
                      <MapPin size={12} className="flex-shrink-0 text-text-muted" />
                      <span>
                        {selectedOffer.partner.address}
                        {selectedOffer.partner.city && `, ${selectedOffer.partner.city}`}
                      </span>
                    </p>
                  )}
                </div>

                {/* Right: Discount badge */}
                <div className="flex items-start pt-1">
                  <span className="inline-block px-3 py-1.5 bg-success/10 text-success text-base font-bold rounded-lg whitespace-nowrap">
                    {selectedOffer.discountType === "percent"
                      ? `${selectedOffer.discountValue}%`
                      : typeof selectedOffer.benefitText === "object"
                        ? selectedOffer.benefitText[currentLang as "ro" | "en"] || selectedOffer.benefitText.ro
                        : selectedOffer.benefitText}
                  </span>
                </div>
              </div>

              {/* Actions row: Buton + Close */}
              <div className="flex items-center gap-2 pt-3 border-t border-border">
                <Link
                  to="/app/offers/$offerId"
                  params={{ offerId: selectedOffer._id }}
                  className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
                >
                  {currentLang === "ro" ? "Vezi oferta" : "View offer"}
                  <CaretRight size={16} />
                </Link>
                <button
                  onClick={() => setSelectedOffer(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  aria-label={currentLang === "ro" ? "Închide" : "Close"}
                >
                  <X size={20} className="text-text-muted" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Map - fullscreen behind header and bottombar */}
      <div className="absolute inset-0 z-0">
        <MapContainer
          center={userLocation || defaultCenter}
          zoom={userLocation ? 15 : 13}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
          key={mapTheme} // Force re-render when theme changes
        >
          <MapInitializer />
          <MapThemeLayer key={mapTheme} theme={mapTheme} />

          {bounds && !locationActive && <FitBounds bounds={bounds} />}
          <LocationButton
            onLocationUpdate={handleLocationUpdate}
            isActive={locationActive}
          />

          {/* Marker pentru locația utilizatorului */}
          {locationActive && userLocation && (
            <Marker
              position={userLocation as [number, number]}
              icon={userLocationIcon}
            >
              <Popup>
                <div className="text-center">
                  <p className="font-semibold text-text">
                    {currentLang === "ro" ? "Locația ta" : "Your location"}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}

          {filteredOffers?.map((offer) =>
            offer.partner?.lat && offer.partner?.lng ? (
              <CenteredMarker
                key={offer._id}
                position={[offer.partner.lat, offer.partner.lng]}
                icon={customIcon}
                offer={offer}
                onSelect={setSelectedOffer}
              />
            ) : null
          )}
        </MapContainer>
      </div>

      {/* Nearest offers sheet */}
      <AnimatePresence>
        {locationActive && nearestOffers.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[1000]"
            onClick={() => setLocationActive(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h5 className="font-semibold text-text">
                  {currentLang === "ro"
                    ? "Cele mai apropiate oferte"
                    : "Nearest offers"}
                </h5>
                <button
                  onClick={() => setLocationActive(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={20} className="text-text-muted" />
                </button>
              </div>

              {/* Offers list */}
              <div className="p-4 overflow-y-auto">
                {nearestOffers.map((item, index) => (
                  <Link
                    key={item.offer._id}
                    to="/app/offers/$offerId"
                    params={{ offerId: item.offer._id }}
                    onClick={() => setLocationActive(false)}
                    className="block p-3 rounded-xl mb-2 bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h5 className="font-semibold text-text">
                          {item.offer.partner?.name}
                        </h5>
                        <p className="text-sm text-text-light mt-1">
                          {item.offer.category?.name}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <MapPin size={16} className="text-text-muted" />
                          <span className="text-xs text-text-muted">
                            {item.distance < 1
                              ? `${Math.round(item.distance * 1000)}m`
                              : `${item.distance.toFixed(2)}km`}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="px-2 py-1 bg-success/10 text-success text-sm font-bold rounded">
                          {item.offer.discountType === "percent"
                            ? `${item.offer.discountValue}%`
                            : typeof item.offer.benefitText === "object"
                              ? item.offer.benefitText[
                                  currentLang as "ro" | "en"
                                ] || item.offer.benefitText.ro
                              : item.offer.benefitText}
                        </span>
                        <span className="text-xs text-text-muted">#{index + 1}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter Sheet */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[1000]"
            onClick={() => setShowFilters(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h5 className="font-semibold text-text">
                {currentLang === "ro" ? "Filtrează" : "Filter"}
              </h5>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} className="text-text-muted" />
              </button>
            </div>

            {/* Categories */}
            <div className="p-4 overflow-y-auto">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setShowFilters(false);
                }}
                className={`w-full p-3 rounded-xl mb-2 text-left flex items-center gap-3 ${
                  !selectedCategory
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-text"
                }`}
              >
                <Percent size={20} />
                <span className="font-medium">
                  {currentLang === "ro" ? "Toate categoriile" : "All categories"}
                </span>
              </button>

              {categories && categories.length > 0 ? (
                categories.map((category) => (
                  <button
                    key={category._id}
                    onClick={() => {
                      setSelectedCategory(category.slug);
                      setShowFilters(false);
                    }}
                    className={`w-full p-3 rounded-xl mb-2 text-left flex items-center gap-3 ${
                      selectedCategory === category.slug
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-text"
                    }`}
                  >
                    <span className="font-medium">{category.name}</span>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-text-muted">
                  <p className="text-sm">
                    {currentLang === "ro" ? "Nu sunt categorii disponibile" : "No categories available"}
                  </p>
                </div>
              )}
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
